import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormField,
  Modal,
  RadioGroup,
  Select,
  SpaceBetween,
  type SelectProps,
} from '@amzn/awsui-components-react';
import { getIvtClient, ListMotionsCommand, type MotionListItem } from '../../../client/IvtHubClient';
import {
  MotionCreateFields,
  useMotionCreateForm,
  type MotionCreateValues,
} from '../../../components/MotionCreateForm/MotionCreateForm';

/**
 * `+ Motion` on an account row — pick the Motion to claim this opportunity into, or create one.
 * Figma: the Add Motion dialog on the STO page (Select Motion / Create Motion).
 *
 * Nothing is written here, the same shape as Add Lighthouse Account: Confirm hands the choice to
 * the page and closes, and the page reports the outcome above the block (`useMotionClaim`).
 *
 * Deliberately small (user, 2026-09-30): this may be replaced by a lighter flow on the
 * opportunity-asset relevance row, so everything about it stays inside this dialog.
 *   - Select lists the Motions of the page's industry, and no finer: a Motion's assets are not
 *     on `ListMotions`, so the comp's "Motion Name (Asset Name)" shows the asset COUNT instead.
 *   - Create is the Motions page's own form, defaulted to the page's industry and to the row's
 *     recommended assets, so the common case needs only a name.
 */

export type AddMotionChoice =
  | { kind: 'existing'; motionId: string; motionName: string }
  | { kind: 'new'; values: MotionCreateValues };

export interface AddMotionModalProps {
  /** Names the row the dialog was opened from. */
  oppName: string;
  /** The page's industry: scopes Select, and defaults Create. */
  industry: string;
  /** The caller's permitted industries, for Create's industry list. */
  allowedIndustries: string[] | null;
  /** The row's recommendations, pre-ticked in Create. */
  recommendedAssetIds: readonly string[];
  onDismiss: () => void;
  onConfirm: (choice: AddMotionChoice) => void;
}

type Mode = 'select' | 'create';

export function AddMotionModal({
  oppName,
  industry,
  allowedIndustries,
  recommendedAssetIds,
  onDismiss,
  onConfirm,
}: AddMotionModalProps) {
  const [mode, setMode] = useState<Mode>('select');

  const [motions, setMotions] = useState<MotionListItem[]>([]);
  const [motionsState, setMotionsState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [picked, setPicked] = useState<SelectProps.Option | null>(null);

  useEffect(() => {
    let cancelled = false;
    getIvtClient()
      .send(new ListMotionsCommand({ industries: [industry] }))
      .then((out) => {
        if (cancelled) return;
        setMotions(out.motions ?? []);
        setMotionsState('ready');
      })
      .catch(() => {
        if (!cancelled) setMotionsState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [industry]);

  const form = useMotionCreateForm({
    allowedIndustries,
    initialIndustry: industry,
    initialAssetIds: recommendedAssetIds,
  });

  const motionOptions: SelectProps.Option[] = motions.flatMap((m) =>
    m.motionId
      ? [
          {
            value: m.motionId,
            label: m.name ?? m.motionId,
            description: `${m.assetCount ?? 0} 个资产`,
          },
        ]
      : [],
  );

  const choice: AddMotionChoice | null =
    mode === 'select'
      ? picked?.value
        ? { kind: 'existing', motionId: picked.value, motionName: picked.label ?? picked.value }
        : null
      : form.values
        ? { kind: 'new', values: form.values }
        : null;

  return (
    <Modal
      visible
      onDismiss={onDismiss}
      header="Add Motion"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={choice === null}
              onClick={() => choice && onConfirm(choice)}
            >
              Confirm
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <Box color="text-body-secondary">将机会「{oppName}」加入 Motion</Box>
        <RadioGroup
          direction="horizontal"
          value={mode}
          onChange={({ detail }) => setMode(detail.value as Mode)}
          items={[
            { value: 'select', label: 'Select Motion' },
            { value: 'create', label: 'Create Motion' },
          ]}
        />
        {mode === 'select' ? (
          <FormField label="Select Motion">
            <Select
              selectedOption={picked}
              onChange={({ detail }) => setPicked(detail.selectedOption)}
              options={motionOptions}
              placeholder="Motion Name"
              filteringType="auto"
              statusType={motionsState === 'ready' ? 'finished' : motionsState === 'failed' ? 'error' : 'loading'}
              loadingText="加载中…"
              errorText="Motion 列表加载失败"
              empty={`${industry} 下还没有 Motion，可以新建一个`}
            />
          </FormField>
        ) : form.loading ? (
          <Box padding={{ vertical: 'l' }} textAlign="center">
            加载中…
          </Box>
        ) : (
          <SpaceBetween size="l">
            {form.loadError && <Box color="text-status-error">⚠️ {form.loadError}</Box>}
            <MotionCreateFields form={form} />
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Modal>
  );
}
